import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { clearLoginSession } from "../../utils/auth";
import MobileMoreSheet from "./mobile_more_sheet";
// DAS-151: 버전 v1 고정 — VariantToggle(V1/V2/V3 선택 버튼) 노출 비활성화.
// 재활성화 시 아래 import와 사이드바 헤더의 <VariantToggle /> 렌더를 복원하고
// utils/variantState.js의 VERSION_LOCKED를 false로 변경한다.
// import VariantToggle from "./variant_toggle";
import { HexLogo } from "./claude_primitives";

/* ─── Icon helpers ─── */
const Icon = ({ children, className = "h-5 w-5" }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className={className}
    >
        {children}
    </svg>
);

const DashboardIcon = (p) => (
    <Icon {...p}>
        <path d="M4 19h16" />
        <path d="M4 15l4-4 4 3 6-7" />
    </Icon>
);

const HistoryIcon = (p) => (
    <Icon {...p}>
        <path d="M3 12a9 9 0 1 0 9-9" />
        <path d="M3 3v6h6" />
        <path d="M12 7v5l3 3" />
    </Icon>
);

const BacktestIcon = (p) => (
    <Icon {...p}>
        <path d="M4 7h16" />
        <path d="M4 12h16" />
        <path d="M4 17h16" />
        <circle cx="8" cy="7" r="1" />
        <circle cx="8" cy="12" r="1" />
        <circle cx="8" cy="17" r="1" />
    </Icon>
);

/* 플라스크 아이콘 — Research 메뉴 */
const ResearchIcon = (p) => (
    <Icon {...p}>
        <path d="M9 3h6v8l3.5 6.5a1 1 0 0 1-.9 1.5H6.4a1 1 0 0 1-.9-1.5L9 11V3z" />
        <path d="M6.5 3h11" />
        <path d="M8 15h8" />
    </Icon>
);

/* 서버 랙 아이콘 — Server 메뉴 (DAS-129) */
const ServerIcon = (p) => (
    <Icon {...p}>
        <rect x="3" y="4" width="18" height="6" rx="1.5" />
        <rect x="3" y="14" width="18" height="6" rx="1.5" />
        <path d="M7 7h.01" />
        <path d="M7 17h.01" />
    </Icon>
);


const WalletIcon = (p) => (
    <Icon {...p}>
        <rect x="2" y="6" width="20" height="14" rx="2" />
        <path d="M2 10h20" />
        <circle cx="16" cy="14" r="1.5" fill="currentColor" stroke="none" />
    </Icon>
);

const MaintenanceIcon = (p) => (
    <Icon {...p}>
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.8-3.8a6 6 0 0 1-7.9 7.9L8 19a2 2 0 1 1-3-3l5.6-5.6a6 6 0 0 1 7.9-7.9l-3.7 3.8z" />
    </Icon>
);

const UserIcon = (p) => (
    <Icon {...p}>
        <path d="M20 21a8 8 0 0 0-16 0" />
        <circle cx="12" cy="7" r="4" />
    </Icon>
);

const LogoutIcon = (p) => (
    <Icon {...p}>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="M16 17l5-5-5-5" />
        <path d="M21 12H9" />
    </Icon>
);

const ChevronLeft = (p) => (
    <Icon {...p}>
        <path d="M15 18l-6-6 6-6" />
    </Icon>
);

const ChevronRight = (p) => (
    <Icon {...p}>
        <path d="M9 18l6-6-6-6" />
    </Icon>
);

/* ─── Logo (통일: HexLogo + MyDashboard — DAS-68) ─── */
const Logo = ({ collapsed = false }) => (
    <div className="flex items-center gap-2.5">
        <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border-[1.5px] border-content"
            aria-hidden="true"
        >
            <HexLogo size={18} stroke="var(--color-content)" accent="var(--color-brand)" />
        </div>
        {!collapsed && (
            <span className="whitespace-nowrap text-sm font-bold tracking-wide text-content">
                MyDashboard
            </span>
        )}
    </div>
);


/* ─── Nav items config ─── */
const navItems = [
    { key: "dashboard",    label: "Dashboard",   to: "/dashboard",    icon: DashboardIcon },
    { key: "history",      label: "History",      to: "/history",      icon: HistoryIcon },
    { key: "backtest",     label: "Backtest",     to: "/backtest",     icon: BacktestIcon },
    { key: "exchange_noti", label: "Wallet",      to: "/exchange-noti", icon: WalletIcon },
    { key: "server",       label: "Server",        to: "/server",       icon: ServerIcon },
    { key: "research",     label: "Research",      to: "/research",     icon: ResearchIcon },
    { key: "maintenance",  label: "Maintenance",  to: "/maintenance",  icon: MaintenanceIcon },
    { key: "my_page",      label: "My Page",      to: "/my_page",      icon: UserIcon },
];

/* Mobile bottom tabs: 4 primary + More sheet (키 기반으로 인덱스 의존 제거) */
const byKey = (k) => navItems.find((i) => i.key === k);
const primaryMobileItems = ["dashboard", "history", "research", "my_page"].map(byKey);
const moreMobileItems    = ["backtest", "exchange_noti", "server", "maintenance"].map(byKey);

/* ─── NavBar ─── */
const NavBar = ({ collapsed, onCollapse }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isCollapsed = Boolean(collapsed);

    const activeKey = navItems.find((it) => location.pathname.startsWith(it.to))?.key || "";

    const handleToggle = () => {
        if (typeof onCollapse === "function") onCollapse(!isCollapsed);
    };

    const handleLogout = () => {
        clearLoginSession();
        navigate("/login");
    };

    return (
        <>
            {/* ══════════ PC Sidebar ══════════ */}
            <aside
                className={`
                    hidden sm:flex fixed left-0 top-0 z-40 h-screen flex-col
                    bg-surface-card border-r border-surface-border
                    transition-[width] duration-200 ease-out
                    ${isCollapsed ? "w-[56px]" : "w-[220px]"}
                `}
            >
                {/* Logo + theme dot + collapse toggle */}
                <div className={`flex items-center h-14 px-3 ${isCollapsed ? "justify-center" : "justify-between"}`}>
                    <div className="flex items-center gap-1.5">
                        <Link to="/dashboard" className="transition-opacity hover:opacity-80">
                            <Logo collapsed={isCollapsed} />
                        </Link>
                        {/* DAS-151: 버전 v1 고정 — VariantToggle 비활성화 */}
                    </div>
                    {!isCollapsed && (
                        <button
                            type="button"
                            onClick={handleToggle}
                            className="flex h-6 w-6 items-center justify-center rounded-md text-content-muted hover:text-content-secondary hover:bg-surface-hover transition-colors"
                            aria-label="사이드바 접기"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                    )}
                    {isCollapsed && (
                        <button
                            type="button"
                            onClick={handleToggle}
                            className="absolute -right-3 top-5 z-50 flex h-6 w-6 items-center justify-center rounded-full bg-surface-elevated border border-surface-border text-content-muted hover:text-content-secondary shadow-card transition-colors"
                            aria-label="사이드바 펼치기"
                        >
                            <ChevronRight className="h-3 w-3" />
                        </button>
                    )}
                </div>

                {/* Nav links */}
                <nav className="flex-1 space-y-1 px-2 mt-2">
                    {navItems.map((item) => {
                        const isActive = item.key === activeKey;
                        const IconComp = item.icon;
                        return (
                            <Link
                                key={item.key}
                                to={item.to}
                                className={`
                                    group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium
                                    transition-all duration-150
                                    ${isActive
                                        ? "bg-brand-subtle text-brand-light"
                                        : "text-content-secondary hover:bg-surface-hover hover:text-content"
                                    }
                                `}
                            >
                                {/* Active indicator bar */}
                                {isActive && (
                                    <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-brand" />
                                )}
                                <IconComp className={`h-5 w-5 flex-shrink-0 ${isActive ? "text-brand-light" : ""}`} />
                                {!isCollapsed && <span>{item.label}</span>}

                                {/* Tooltip for collapsed state */}
                                {isCollapsed && (
                                    <span className="
                                        pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded-md
                                        bg-surface-elevated px-2.5 py-1.5 text-xs font-medium text-content
                                        shadow-elevated border border-surface-border
                                        opacity-0 transition-opacity group-hover:opacity-100
                                    ">
                                        {item.label}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Logout */}
                <div className="border-t border-surface-border px-2 py-3">
                    <button
                        type="button"
                        onClick={handleLogout}
                        className={`
                            flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium
                            text-content-secondary hover:bg-loss-subtle hover:text-loss transition-colors
                        `}
                    >
                        <LogoutIcon className="h-5 w-5 flex-shrink-0" />
                        {!isCollapsed && <span>Logout</span>}
                    </button>
                </div>
            </aside>

            {/* ══════════ Mobile: Top header ══════════ */}
            <header className="fixed left-0 right-0 top-0 z-50 flex h-12 items-center justify-center bg-surface-card/95 backdrop-blur-md border-b border-surface-border sm:hidden">
                <Link to="/dashboard" aria-label="홈으로 이동" className="transition-opacity hover:opacity-80">
                    <Logo />
                </Link>
                {/* UX-012: 모바일 로그아웃 버튼 */}
                <button
                    type="button"
                    onClick={handleLogout}
                    aria-label="로그아웃"
                    className="absolute right-3 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-lg text-content-muted hover:bg-loss-subtle hover:text-loss transition-colors"
                >
                    <LogoutIcon className="h-4 w-4" />
                </button>
            </header>

            {/* ══════════ Mobile: Bottom tab bar ══════════ */}
            <nav className="fixed bottom-0 left-0 right-0 z-50 bg-surface-card/95 backdrop-blur-md border-t border-surface-border sm:hidden pb-safe">
                <div className="flex h-14 items-stretch">
                    {primaryMobileItems.map((item) => {
                        const isActive = item.key === activeKey;
                        const IconComp = item.icon;
                        return (
                            <Link
                                key={item.key}
                                to={item.to}
                                aria-label={item.label}
                                className={`
                                    relative flex flex-1 flex-col items-center justify-center gap-0.5
                                    transition-colors duration-150 min-h-[44px]
                                    ${isActive ? "text-brand-light" : "text-content-muted"}
                                `}
                            >
                                {/* Active top indicator */}
                                {isActive && (
                                    <span className="absolute top-0 left-1/2 -translate-x-1/2 h-[2px] w-8 rounded-b-full bg-brand" />
                                )}
                                <IconComp className={`h-5 w-5 ${isActive ? "text-brand-light" : ""}`} />
                                <span className="text-xs font-medium leading-tight">{item.label}</span>
                            </Link>
                        );
                    })}
                    {/* 5번째: More 시트 (Backtest, Maintenance, Theme) */}
                    <MobileMoreSheet
                        items={moreMobileItems}
                        onLogout={handleLogout}
                        activeKey={activeKey}
                    />
                </div>
            </nav>
        </>
    );
};

export default NavBar;
